"use client";

import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, Bot, User, CheckCircle2, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";

import ReactMarkdown from "react-markdown";

// Security: Custom link component to prevent reverse tabnabbing
const SecureChatLink = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const { language } = useLanguage();
    const isExternal = href?.startsWith('http') || href?.startsWith('//');

    if (isExternal) {
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-primary break-all"
                {...props}
            >
                {children}
                <ExternalLink className="h-3 w-3 ml-0.5 inline-block opacity-70" aria-hidden="true" />
                <span className="sr-only">
                    {language === 'de' ? '(öffnet in neuem Tab)' : '(opens in a new tab)'}
                </span>
            </a>
        );
    }
    return <a href={href} className="underline underline-offset-2 hover:text-primary break-all" {...props}>{children}</a>;
};

interface Message {
    id: string;
    role: "assistant" | "user";
    content: string;
    isEvaluation?: boolean;
}

interface EvaluationChatProps {
    scenarioId: string;
    initialMessage: string;
    context: string; // The "Task" or "Scenario" description
    idealAnswer: string; // The rubric/ideal answer content
    token: string;
    onLog: (role: "user" | "assistant", content: string) => void;
}

export function EvaluationChat({ scenarioId, initialMessage, context, idealAnswer, token, onLog }: EvaluationChatProps) {
    const [messages, setMessages] = useState<Message[]>([
        { id: "init", role: "assistant", content: initialMessage }
    ]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessageContent = input;
        const userMessage: Message = {
            id: Date.now().toString(),
            role: "user",
            content: userMessageContent
        };

        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);

        // Save User Message
        onLog("user", userMessageContent);

        try {
            // Call Supabase Edge Function
            const { data, error } = await supabase.functions.invoke('evaluate-answer', {
                body: {
                    messages: [...messages, userMessage],
                    scenarioId
                },
                headers: {
                    "x-access-token": token
                }
            });

            if (error) {
                console.error('Supabase function error:', error);
                throw error;
            }

            if (data && data.reply) {
                const aiMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: data.reply
                };
                setMessages(prev => [...prev, aiMessage]);
                // Save Assistant Message
                onLog("assistant", data.reply);
            } else {
                // Fallback
                const fallbackText = "I'm listening. (Note: Supabase Edge Function 'evaluate-answer' needs to be deployed to generate real responses).";
                const simulationMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: fallbackText
                };
                setMessages(prev => [...prev, simulationMessage]);
            }

        } catch (err) {
            toast.error("Failed to get response. Is the Supabase function deployed?");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-[600px] border rounded-xl overflow-hidden bg-background shadow-sm">
            {/* Header */}
            <div className="bg-muted/30 p-4 border-b flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-full">
                    <Bot className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <h4 className="font-semibold text-sm">Competence AI Evaluator</h4>
                    <p className="text-xs text-muted-foreground">Probing your understanding...</p>
                </div>
            </div>

            {/* Messages */}
            <div
                className="flex-1 overflow-y-auto p-4 space-y-6 bg-slate-50/50 dark:bg-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset"
                ref={scrollRef}
                tabIndex={0}
                aria-label="Chat messages"
            >
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={cn(
                            "flex gap-3 max-w-[85%]",
                            msg.role === "user" ? "ml-auto flex-row-reverse" : ""
                        )}
                    >
                        <div className={cn(
                            "h-8 w-8 rounded-full flex items-center justify-center shrink-0 border shadow-sm",
                            msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground"
                        )}>
                            {msg.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                        </div>

                        <div className={cn(
                            "rounded-2xl p-4 text-sm shadow-sm leading-relaxed",
                            msg.role === "user"
                                ? "bg-primary text-primary-foreground rounded-tr-sm"
                                : "bg-card border rounded-tl-sm text-foreground"
                        )}>
                            <ReactMarkdown
                                components={{
                                    p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                                    ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2" {...props} />,
                                    ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                                    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                    strong: ({ node, ...props }) => <span className="font-bold" {...props} />,
                                    a: SecureChatLink
                                }}
                            >
                                {msg.content}
                            </ReactMarkdown>
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex gap-3 max-w-[85%]">
                        <div className="h-8 w-8 rounded-full bg-card flex items-center justify-center shrink-0 border">
                            <Bot className="h-4 w-4 text-muted-foreground animate-pulse" />
                        </div>
                        <div className="bg-card border rounded-2xl p-4 rounded-tl-sm flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Thinking...</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Input */}
            <div className="p-4 bg-background border-t">
                <div className="relative">
                    <Textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your answer here..."
                        className="pr-12 min-h-[60px] resize-none py-3"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                    <Button
                        size="icon"
                        onClick={handleSend}
                        disabled={isLoading || !input.trim()}
                        className="absolute right-2 top-2 h-8 w-8"
                        aria-label="Send message"
                    >
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
                <div className="text-[10px] text-muted-foreground text-center mt-2">
                    AI can make mistakes. Review the "Ideal Solution" manually if unsure.
                </div>
            </div>
        </div>
    );
}
